import { useState } from 'react';
import { Button, Header, Image, Modal, Segment } from 'semantic-ui-react';
import ConfirmationModal from '../../../../../components/confirmationModal';
import ImageUpload from '../../../../../components/imageUpload';
import { RESPONSE_STATUS } from '../../../../../constnats/general';
import useModalState from '../../../../../hooks/useModalState';
import Alert from '../../../../../utils/alert';
import { AssetUtils } from '../../../../../utils/assetsUtils';
import CompanyRepo from '../../../../../utils/repository/companyRepo';

const imagePlaceholderUrl = 'https://react.semantic-ui.com/images/wireframe/image.png';

type AdministrativeAssetsPropsType = {
    companyId: number;
};

function AdministrativeAssets({ companyId }: AdministrativeAssetsPropsType) {
    const [imageExists, setImageExists] = useState(true);
    const [imageKey, setImageKey] = useState(() => Date.now());
    const [isUploading, setIsUploading] = useState(false);
    const uploadImageModalState = useModalState();
    const confirmationModalState = useModalState();

    const logoFileName = AssetUtils.buildLogoName();
    const logoURL = `${AssetUtils.buildLogoURL(companyId)}?v=${imageKey}`;

    const handleImageError = () => {
        setImageExists(false);
    };

    const handleLogoUpload = async (file: File) => {
        setIsUploading(true);

        if (imageExists) {
            try {
                await deleteLogo(true);
            } catch (error) {
                Alert.error('Failed to replace existing logo');
                setIsUploading(false);
                uploadImageModalState.closeModal();
                return;
            }
        }

        const data = new FormData();
        data.append('file', file);

        CompanyRepo.uploadAsset(companyId, { fileName: logoFileName, data }, (res: any) => {
            setIsUploading(false);

            if (res.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success(res.message);
                setImageExists(true);
                setImageKey(Date.now());
            } else {
                Alert.warn(res.message);
            }
            uploadImageModalState.closeModal();
        });
    };

    const deleteLogo = (silent = false) => {
        return new Promise((res, rej) => {
            CompanyRepo.deleteAsset(
                companyId,
                { fileName: logoFileName },
                (result: any) => {
                    if (result.status === RESPONSE_STATUS.SUCCESS) {
                        if (!silent) {
                            Alert.success(result.message);
                        }
                        setImageExists(false);
                        setImageKey(Date.now());
                        if (!silent) {
                            confirmationModalState.closeModal();
                        }
                        res(result.data);
                    } else {
                        Alert.warn(result.message);
                        rej(result.message);
                    }
                },
                rej,
            );
        });
    };

    return (
        <Segment>
            <Segment>
                <Header as="h5">Company Logo</Header>
                <div className="flex gap-10">
                    <Image size="small" src={imageExists ? logoURL : imagePlaceholderUrl} onError={handleImageError} />
                    <div className="flex flex-column gap-10">
                        <Button
                            primary
                            icon="edit"
                            onClick={uploadImageModalState.openModal}
                            loading={isUploading}
                            disabled={isUploading}
                        />
                        {imageExists && (
                            <Button
                                icon="trash"
                                color="red"
                                onClick={confirmationModalState.openModal}
                                disabled={isUploading}
                            />
                        )}
                    </div>
                </div>
            </Segment>
            <Modal size="small" open={uploadImageModalState.open} onClose={uploadImageModalState.closeModal}>
                <Modal.Content>
                    <div className="flex flex-justify-center">
                        <ImageUpload onUpload={handleLogoUpload} />
                    </div>
                </Modal.Content>
            </Modal>
            <ConfirmationModal
                msg="Are you sure you want to delete this image"
                onCancel={confirmationModalState.closeModal}
                open={confirmationModalState.open}
                onConfirm={() => deleteLogo(false)}
            />
        </Segment>
    );
}

export default AdministrativeAssets;
